import { styled } from '@ori-ui/mui';
import { useIsMobile } from '@ori/presentation-hooks';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import React from 'react';

import { useBannerColors } from '../hooks';
import { useTopAreaStore } from '../stores/TopAreaStore';
import { BannerImage } from './BannerImage';
import { BannerVideo } from './BannerVideo';
import { Breadcrumbs } from './Breadcrumbs';

interface BannerProps {
  hasNoBreadcrumbs?: boolean;
  isPortalMode?: boolean;
}

interface StyledProps {
  backgroundColor?: string;
  color?: string;
  hasNoContent?: boolean;
  hasNoMedia?: boolean;
  isPortalMode?: boolean;
}

const StyledBanner = styled('div')<StyledProps>(({ backgroundColor, color, hasNoContent, hasNoMedia, theme }) => ({
  background: backgroundColor,
  color,
  height: 'unset',
  maxHeight: '500px',
  minHeight: '100px',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up(768)]: {
    height: 'unset',
    marginBottom: theme.spacing(5),
  },
  ...(hasNoMedia && {
    height: '180px',
    [theme.breakpoints.up(768)]: {
      height: '280px',
      marginBottom: theme.spacing(5),
    },
  }),
  ...(hasNoContent && { minHeight: 0 }),
}));

const StyledBreadcrumbs = styled('div')<StyledProps>(({ hasNoContent, theme }) => ({
  display: 'inline-block',
  left: '15px',
  position: 'absolute',
  top: '20px',
  zIndex: 10,
  [theme.breakpoints.up(768)]: {
    left: '40px',
    margin: 'unset',
    position: 'absolute',
    top: '25px',
  },
  ...(hasNoContent && {
    [theme.breakpoints.up(768)]: {
      margin: theme.spacing(5, 0, 0, 8),
      position: 'static',
    },
  }),
}));

const StyledContent = styled('div')<StyledProps>(({ hasNoContent, theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  left: '50%',
  position: 'absolute',
  transform: 'translate(-50%, -50%)',
  top: '50%',
  width: '100%',
  zIndex: 3,
  ...(hasNoContent && {
    left: 0,
    padding: theme.spacing(10, 0),
    position: 'relative',
    top: 0,
    [theme.breakpoints.up(768)]: {
      left: '50%',
      padding: 0,
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      top: '50%',
    },
  }),
}));

const StyledTitle = styled('div')(({ theme }) => ({
  display: '-webkit-box',
  fontFamily: 'SansCondensedOri, SansCondensedOriFallback, SansOri, SansOriFallback, sans-serif',
  fontSize: theme.typography.h4.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  overflow: 'hidden',
  textAlign: 'center',
  textOverflow: 'ellipsis',
  textTransform: 'uppercase',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  width: '80%',
  [theme.breakpoints.up(768)]: {
    fontSize: theme.typography.pxToRem(60),
    WebkitLineClamp: 2,
  },
}));

const StyledMediaContainer = styled('div')<StyledProps>(({ isPortalMode, theme }) => ({
  display: 'block',
  height: 180,
  opacity: 1,
  position: 'relative',
  '&:before': {
    content: '""',
    display: 'block',
    paddingTop: 180,
    width: '100%',
  },
  [theme.breakpoints.up(768)]: {
    height: 280,
    marginRight: 0,
    transform: 'none',
    '&:before': {
      paddingTop: 280,
    },
  },
  [theme.breakpoints.up(1366)]: {
    height: 'auto',
    ...(!isPortalMode && {
      '&:before': {
        paddingTop: '20.5%',
      },
    }),
  },
}));

export const Banner: FC<BannerProps> = ({ hasNoBreadcrumbs, isPortalMode }) => {
  const { backgroundVideoUrl, backgroundImageUrl, bannerHeadlineText, isSubnavNested } = useTopAreaStore();

  const colors = useBannerColors();
  const hasNoContent = !backgroundVideoUrl && !backgroundImageUrl && !bannerHeadlineText;
  const hasNoMedia = !backgroundVideoUrl && !backgroundImageUrl;
  const hasNoTitle = Boolean(!bannerHeadlineText);
  const isMobile = useIsMobile();
  const { getTestId } = useTestId();

  if (hasNoContent && isMobile && isSubnavNested) {
    return null;
  }

  const styledProps = {
    ...colors,
    hasNoMedia,
    hasNoTitle,
    isPortalMode,
  };

  return (
    <StyledBanner {...styledProps}>
      <StyledBreadcrumbs
        {...styledProps}
        data-testid={getTestId('breadcrumbs')}
      >
        {!hasNoBreadcrumbs && <Breadcrumbs />}
      </StyledBreadcrumbs>
      <StyledContent {...styledProps}>
        <StyledTitle data-testid={getTestId('title')}>{bannerHeadlineText}</StyledTitle>
      </StyledContent>
      {!hasNoMedia && (
        <StyledMediaContainer {...styledProps}>
          <BannerVideo />
          <BannerImage />
        </StyledMediaContainer>
      )}
    </StyledBanner>
  );
};
